import _ from "lodash";
import shortid from 'shortid';

import { ASSET, CAROUSEL, CONTENT, CONTENT_CARD, GROUP, HOTSPOT, MENU, SECTION, SEEK } from 'constants/widgetTypes';

/**
 *  Generate the widget request from the palette drop
*/
export function generateWidgetsFromPaletteRequest(editor, request, parent){
  if(!request){
    return request;
  }

  var widgets = new Array();
  if(_.size(request) > 0){
    request.forEach(childRequest => {
      _generateWidgetsFromPaletteRequest(childRequest, parent, widgets);
    });
  }
  
  return widgets;
}

/**
 * Calculate the order for widgets with parents
 * 
*/
export function calculateOrderForWidgets(widgets, parent){
  if(_.size(widgets) == 0){
    return widgets;
  }

  widgets.filter(widget => widget.parent === parent).sort((w1, w2) => {
    const o1 = _.get(w1, 'order') || 1;
    const o2 = _.get(w2, 'order') || 1;

    return o1 - o2;
  }).forEach((widget, index) => {
    _.set(widget, 'order', index + 1);
    calculateOrderForWidgets(widgets, widget.uid);
  });

  return widgets;
}


/**
 * Get attached widget new layout
 *
 */
export function getAttachedWidgetLayout({ 
  widgets,
  parent,
  layout,
  widgetProperties,
  widgetType,
  makeFrame
}) {
  if (!parent || !layout) {
    return widgetProperties;
  }
  const widgetProps = _.find(widgets, function (data) {
    return data.uid == parent;
  });

  const adjustedPosition = (breakpoint) => {
    const widgetWidth = parseInt(
      _.get(widgetProps, `properties.${breakpoint}.layout.width`)
    );
    const widgetHeight = parseInt(
      _.get(widgetProps, `properties.${breakpoint}.layout.height`)
    );

    const newX = parseInt(layout.transform.translate.split(",")[0]);
    const newY = parseInt(layout.transform.translate.split(",")[1]);

    let adjustedX = newX;
    let adjustedY = newY;

    if(makeFrame) {
      // Adjust x position only if it overflows the container
      if (newX < 0 || newX > widgetWidth) {
        adjustedX = (breakpoint == 'mobile' && widgetType == CAROUSEL ) ? 0 : newX;
      }
    } else {
      // Adjust x position if it overflows the width
      if ((newX > widgetWidth) || (newX < widgetWidth)) {
        adjustedX = (breakpoint == 'mobile' && widgetType == CAROUSEL ) ? 0 :  Math.floor(widgetWidth / 2);
      }
      // Adjust y position if it overflows the height
      if ((newY  > widgetHeight) || (newY < widgetHeight)) {
        adjustedY = Math.floor(widgetHeight / 2);
      }
    }

    // Update the transform property with adjusted positions
    return {
      transform: {
        translate: `${adjustedX}px, ${adjustedY}px`,
        rotate: layout.transform.rotate,
        scale: layout.transform.scale,
      },
      width: _.get(widgetProperties, `${breakpoint}.layout.width`),
      height: _.get(widgetProperties, `${breakpoint}.layout.height`),
    };
  };

  const desktopLayout = adjustedPosition("desktop");
  const mobileLayout = adjustedPosition("mobile");

  return {
    desktop: {
      ...widgetProperties.desktop,
      layout: desktopLayout,
    },
    mobile: {
      ...widgetProperties.mobile,
      layout: mobileLayout,
    },
  };
}


/**
 * Get the next order for widget in parent
 * 
*/
export function calculateOrderForWidget(widgets, parent){
  if(_.size(widgets) == 0){
    return 1;
  }

  const filtered = widgets.filter(widget => widget.parent == parent).sort((w1, w2) => {
    const o1 = _.get(w1, 'order') || 1;
    const o2 = _.get(w2, 'order') || 1;

    return o2 - o1;
  });

  if(_.size(filtered) == 0){
    return 1;
  }

  const lastOrder = _.get(filtered, '0.order') || 0;
  return lastOrder + 1;
}

/**
 * Get Widgets with ancestor
 * 
*/
export function getWidgetsWithAncestor(widgets, parent){
  if(_.size(widgets) == 0){
    return [];
  }

  var result = new Array();
  
  const children = widgets.filter(widget => widget.parent == parent).map(widget => widget.uid);
  result = [...result, ...children];

  children.forEach(child => {
    const childWidgets = getWidgetsWithAncestor(widgets, child);
    result = [...result, ...childWidgets];
  });

  return result;
}

/**
 * Private Functions
*/

/**
 *  Private function to recursively generate the widget request from the palette drop. 
 *  widgets array will be used to collect all the request generated
*/
function _generateWidgetsFromPaletteRequest(request, parent, widgets){
  if(!request){
    return request;
  }

  if(!widgets){
    const error = "Widgets array cannot be null";
    console.error(error);
    throw new Error(error);
  }

  var widget = _.omit(request, ['widgets', "parent"]);
  var uid = widget.uid;
  if(!uid){
    uid = `widget_${shortid.generate()}`;
  }

  var variableMappings = widget.variableMappings;
  if (variableMappings && _.size(variableMappings) > 0) {
    widget.variableMappings = variableMappings.map(vm => ({
      ...vm,
      id: undefined,
      uid: shortid.generate(),
      widget: uid,
    }));
  }

  widget = {
    ...widget,
    uid: uid,
    parent: parent
  }

  widgets.push(widget);

  if(_.size(request.widgets) > 0){
    request.widgets.forEach(childRequest => {
      _generateWidgetsFromPaletteRequest(childRequest, widget.uid, widgets);
    });
  }
}

/**
 * Find all font with the dom element
*/
export function findFontsFromHTML(html){
  var root = document.createElement('div');
  root.innerHTML = (html || "").trim();

  var fonts = [];
  Array.from(root.children).forEach(child => {
    _findFontsFromElement(child, fonts);
  });

  return fonts;
}

function _findFontsFromElement(element, fonts){
  if(!element){
    return;
  }

  const font = element.style.fontFamily;
  if(font && !_.includes(fonts, font)){
    fonts.push(font);
  }

  Array.from(element.children).forEach(child => {
    _findFontsFromElement(child, fonts);
  });
}


/**
 * Check if the widget inside group
*/
export function isWidgetInsideGroup(widgets, id) {
  const widget = widgets.find(item => item.uid === id);

  if (!widget || widget.type === GROUP) {
    return false;
  }

  const parent = widget.parent;

  if (!parent) {
    return false;
  }

  return _isWidgetInsideGroup(widgets, parent);
}

function _isWidgetInsideGroup(widgets, id) {
  const widget = widgets.find(item => item.uid === id);

  if (widget.type === GROUP) {
    return true;
  }

  const parent = widget.parent;

  if(!parent) {
    return false;
  }

  return _isWidgetInsideGroup(widgets, parent);
}


/**
 * Check if the widget inside group
*/
export function getParentGroupWidget(widgets, id) {
  const widget = widgets.find(item => item.uid === id);

  if (!widget || widget.type === GROUP) {
    return null;
  }

  const parent = widget.parent;

  return _getParentGroupWidget(widgets, parent);
}

function _getParentGroupWidget(widgets, id) {
  const widget = widgets.find(item => item.uid === id);

  if (widget.type === GROUP) {
    return widget;
  }

  const parent = widget.parent;

  if (!parent) {
    return null;
  }

  return _getParentGroupWidget(widget, parent);
}

/**
 * Check if widget is an child in the given parent type
 * @Param editorSession: editor session context
 * @Param widget: id of the widget
 * @Param parent: type of the parent widget
 * 
*/
export function getParentContainerOfType(editorSession, widget, parent){
  const widgetElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${widget}`);
  if(!widgetElement){
    return false;
  }
  
  const container = widgetElement.closest(`[data-widget-type="${parent}"]`);
  return container;
}

/**
 * Check if widget has a hotspot child
 * @Param editorSession: editor session context
 * @Param widget: id of the widget
 * @Param parent: type of the parent widget
 * 
*/
export function isWidgetHasHotspotChild(editorSession, widget) {
  const widgetElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${widget}`);
  if (!widgetElement) {
    return false;
  }

  return widgetElement.hasAttribute('data-hotspot');
}

/**
 * Check if widget childrens has a hotspot child
 * @Param editorSession: editor session context
 * @Param widget: id of the widget
 * 
*/
export function isWidgetChildrensHasHotspotChild(editorSession, widget) {
  const widgetElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${widget}`);
  if (!widgetElement) {
    return false;
  }

  return (
    widgetElement.querySelector('[data-hotspot]') !== null
  );
}

/**
 * Check if topParent is a hotspot for a widget
 * @Param editorSession: editor session context
 * @Param parent: parent element of the widget
 * @Param containerType: type of the parent widget
 * 
*/
export function isTopParentIsHotspot(editorSession, parent, containerType){
  const topParentElement = getParentContainerOfType(editorSession, parent, containerType);

  if (!topParentElement) {
    return false;
  }
  
  return topParentElement.closest(`[data-widget-type="${HOTSPOT}"]`);
}

/**
 * get widget type by id
 * @Param editorSession: editor session context
 * @Param widget: id of the widget
 * @Param parent: type of the parent widget
 * 
*/
export function getWidgetType(editorSession, widget) {
  const widgetElement = document.querySelector(`#${editorSession} .editor_widget_renderer #${widget}`);
  if (!widgetElement) {
    return false;
  }

  return widgetElement.getAttribute('data-widget-type');
}

/**
 * Calcalate the bounds for the element. Generally used to restrict in moveable
 * @Param element: html dom element representing the widget
 * 
*/
export function calculateBoundsForElement(element){
  if(!element){
    return null;
  }

  const pageElement = element?.closest(`.editor_widget_scroll_container`);

  const rect = element?.getBoundingClientRect();
  const parentRect = pageElement?.getBoundingClientRect();

  const left = rect?.left - parentRect?.left;
  const top = pageElement?.scrollTop + rect?.top - parentRect?.top;

  const bounds = { 
    left: left, 
    right: left + rect?.width, 
    top: top, 
    bottom: top + rect?.height
  }

  return bounds;
}


/**
 * Finds the top-level page widget for a widget element
 */

export function findPageWidgetForElement(widget) {
  const element = document.getElementById(widget)

  if (!element) {
    return null;
  }

  const page = element.closest(
    '[data-widget-type="cleverstory.v2.widgets.page"]'
  );

  return page?.id;
}